/**
 * Currency Translator for Bucks Currency Converter
 * Automatically translates currency names when RTL or Arabic is detected
 * 
 * Usage in Shopify:
 * 1. Upload currency-translations.json to Assets folder
 * 2. Upload this file to Assets folder
 * 3. Add to theme.liquid before </body>:
 *    <script src="{{ 'currency-translator.js' | asset_url }}" defer></script>
 */
(function() {
  'use strict';
  // ============================================
  // CONFIGURATION
  // ============================================
  const CONFIG = {
    // Path to translations JSON file (adjust for your Shopify setup)
    translationsPath: '/assets/currency-translations.json',
    
    // Selectors to search for currency text (add more if needed)
    selectors: [
      '[class*="currency"]',
      '[class*="Currency"]',
      '[id*="currency"]',
      '[id*="Currency"]',
      'select option',
      '[class*="bucks"]',
      '[class*="Bucks"]',
      '[data-currency]',
      '.currency-switcher',
      '.currency-selector',
      '.currency-dropdown',
      'button',
      'span',
      'li',
      'a'
    ],
    
    // Retry settings for dynamic content
    maxRetries: 10,
    retryInterval: 500, // ms
    
    // MutationObserver settings
    observeSubtree: true,
    observeChildList: true,
    observeCharacterData: true,
    
    // Debug mode (set to true to see console logs)
    debug: false
  };
  // ============================================
  // STATE
  // ============================================
  let translations = {};
  let isInitialized = false;
  let retryCount = 0;
  let observer = null;
  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  
  function log(...args) {
    if (CONFIG.debug) {
      console.log('[CurrencyTranslator]', ...args);
    }
  }
  function error(...args) {
    console.error('[CurrencyTranslator]', ...args);
  }
  /**
   * Detect if page should use Arabic translations
   * Checks multiple signals: dir attribute, lang attribute, html lang
   */
  function shouldTranslate() {
    const html = document.documentElement;
    const body = document.body;
    
    // Check direction
    const isRTL = html.dir === 'rtl' || 
                  body?.dir === 'rtl' || 
                  getComputedStyle(html).direction === 'rtl' ||
                  getComputedStyle(body).direction === 'rtl';
    
    // Check language
    const lang = html.lang?.toLowerCase() || '';
    const isArabic = lang.startsWith('ar') || 
                     lang === 'ar' ||
                     document.querySelector('html[lang^="ar"]') !== null;
    
    // Check Shopify locale (if available)
    const shopifyLocale = window.Shopify?.locale?.toLowerCase() || '';
    const isShopifyArabic = shopifyLocale.startsWith('ar');
    
    // Check URL for language indicator
    const urlHasArabic = window.location.pathname.includes('/ar') ||
                         window.location.search.includes('locale=ar');
    
    const result = isRTL || isArabic || isShopifyArabic || urlHasArabic;
    
    log('Detection results:', { isRTL, isArabic, isShopifyArabic, urlHasArabic, shouldTranslate: result });
    
    return result;
  }
  /**
   * Load translations from JSON file
   */
  async function loadTranslations() {
    try {
      // Try multiple paths for flexibility
      const paths = [
        CONFIG.translationsPath,
        './currency-translations.json',
        '/currency-translations.json',
        window.Shopify?.routes?.root + 'assets/currency-translations.json'
      ].filter(Boolean);
      for (const path of paths) {
        try {
          log('Trying to load translations from:', path);
          const response = await fetch(path);
          if (response.ok) {
            translations = await response.json();
            log('Translations loaded successfully:', translations);
            return true;
          }
        } catch (e) {
          log('Failed to load from:', path);
        }
      }
      
      // Fallback: Use embedded translations
      log('Using embedded translations as fallback');
      translations = {
        "Auto Location": "تحديد الموقع تلقائياً",
        "Bahraini Dinar (BHD)": "دينار بحريني (دب)",
        "Qatari Riyal (QAR)": "ريال قطري (رق)",
        "Kuwaiti Dinar (KWD)": "دينار كويتي (دك)",
        "Saudi Riyal (SAR)": "ريال سعودي (رس)",
        "United Arab Emirates Dirham (AED)": "درهم إماراتي (دإ)",
        "Omani Riyal (OMR)": "ريال عُماني (رع)",
        "US Dollar (USD)": "دولار أمريكي (دولار)",
        "Euro (EUR)": "يورو (يورو)"
      };
      return true;
      
    } catch (err) {
      error('Failed to load translations:', err);
      return false;
    }
  }
  /**
   * Translate text content of an element
   */
  function translateElement(element) {
    if (!element || element.hasAttribute('data-translated')) return false;
    
    let translated = false;
    
    // Handle text nodes directly
    const childNodes = Array.from(element.childNodes);
    
    childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const originalText = node.textContent.trim();
        
        if (originalText && translations[originalText]) {
          node.textContent = node.textContent.replace(originalText, translations[originalText]);
          translated = true;
          log('Translated:', originalText, '→', translations[originalText]);
        }
      }
    });
    
    // Also check element's direct text content for exact matches
    if (!translated && element.childNodes.length === 1 && element.childNodes[0].nodeType === Node.TEXT_NODE) {
      const text = element.textContent.trim();
      if (translations[text]) {
        element.textContent = translations[text];
        translated = true;
        log('Translated (direct):', text, '→', translations[text]);
      }
    }
    
    // Handle select options
    if (element.tagName === 'OPTION') {
      const text = element.textContent.trim();
      if (translations[text]) {
        element.textContent = translations[text];
        translated = true;
        log('Translated option:', text, '→', translations[text]);
      }
    }
    
    // Mark as translated to avoid re-processing
    if (translated) {
      element.setAttribute('data-translated', 'true');
    }
    
    return translated;
  }
  /**
   * Scan and translate all matching elements
   */
  function translateAll() {
    if (Object.keys(translations).length === 0) {
      log('No translations loaded yet');
      return 0;
    }
    let count = 0;
    const selector = CONFIG.selectors.join(', ');
    
    try {
      const elements = document.querySelectorAll(selector);
      
      elements.forEach(el => {
        if (translateElement(el)) {
          count++;
        }
      });
      
      // Also do a broader search for any text matches
      document.querySelectorAll('*:not(script):not(style):not(meta):not(link)').forEach(el => {
        if (!el.hasAttribute('data-translated')) {
          if (translateElement(el)) {
            count++;
          }
        }
      });
      
      log(`Translated ${count} elements`);
      
    } catch (err) {
      error('Error during translation scan:', err);
    }
    
    return count;
  }
  /**
   * Setup MutationObserver to handle dynamically loaded content
   */
  function setupObserver() {
    if (observer) {
      observer.disconnect();
    }
    observer = new MutationObserver((mutations) => {
      let shouldTranslate = false;
      
      mutations.forEach(mutation => {
        // Check added nodes
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            shouldTranslate = true;
          }
        });
        
        // Check character data changes
        if (mutation.type === 'characterData') {
          shouldTranslate = true;
        }
      });
      
      if (shouldTranslate) {
        // Debounce translations
        clearTimeout(window._currencyTranslateTimeout);
        window._currencyTranslateTimeout = setTimeout(() => {
          translateAll();
        }, 100);
      }
    });
    observer.observe(document.body, {
      subtree: CONFIG.observeSubtree,
      childList: CONFIG.observeChildList,
      characterData: CONFIG.observeCharacterData
    });
    
    log('MutationObserver setup complete');
  }
  /**
   * Retry translation for dynamically loaded content
   */
  function retryTranslation() {
    if (retryCount >= CONFIG.maxRetries) {
      log('Max retries reached');
      return;
    }
    
    retryCount++;
    log(`Retry ${retryCount}/${CONFIG.maxRetries}`);
    
    const translated = translateAll();
    
    if (translated === 0 && retryCount < CONFIG.maxRetries) {
      setTimeout(retryTranslation, CONFIG.retryInterval);
    }
  }
  /**
   * Initialize the translator
   */
  async function init() {
    if (isInitialized) {
      log('Already initialized');
      return;
    }
    log('Initializing Currency Translator...');
    
    // Check if we should translate
    if (!shouldTranslate()) {
      log('Page is not RTL/Arabic, skipping translation');
      return;
    }
    log('RTL/Arabic detected, loading translations...');
    
    // Load translations
    const loaded = await loadTranslations();
    if (!loaded) {
      error('Failed to load translations, aborting');
      return;
    }
    // Initial translation
    translateAll();
    
    // Setup observer for dynamic content
    setupObserver();
    
    // Retry for lazy-loaded content
    setTimeout(retryTranslation, CONFIG.retryInterval);
    
    // Handle Shopify section events (Theme Editor)
    document.addEventListener('shopify:section:load', () => {
      log('Shopify section loaded, re-translating...');
      setTimeout(translateAll, 200);
    });
    
    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        translateAll();
      }
    });
    isInitialized = true;
    log('Initialization complete');
  }
  // ============================================
  // PUBLIC API (for manual control if needed)
  // ============================================
  window.CurrencyTranslator = {
    init: init,
    translate: translateAll,
    addTranslation: (key, value) => {
      translations[key] = value;
      translateAll();
    },
    getTranslations: () => ({ ...translations }),
    setDebug: (enabled) => { CONFIG.debug = enabled; }
  };
  // ============================================
  // AUTO-INITIALIZE
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }
  // Also run after window load (for lazy content)
  window.addEventListener('load', () => {
    setTimeout(translateAll, 500);
  });
})();
